import { useEffect, useRef, useState } from 'react';

import { trackEvent } from '../../../../helpers/analytics';
import {
  useSetupMfaMutation,
  useEnableMfaMutation,
  useGetMfaStatusQuery,
  useRegenerateBackupCodesMutation,
} from '../../../../services/user';

export const ONBOARDING_MFA_STEP = {
  SETUP: 'setup', // QR code + secret + 6-digit code form
  BACKUP_CODES: 'backupCodes', // one-time display of backup codes
};

// Forced-enrollment as an onboarding gate that runs right after the account/password step.
// Unlike the standalone /mfa/setup page (which runs off a pre-session setup token), the
// invitee already has a session here, so this uses the authenticated self-service endpoints.
// `onComplete` resumes the walkthrough once enrollment finishes.
export const useOnboardingMfa = ({ onComplete } = {}) => {
  const [setupMfa, { isLoading: isSettingUp }] = useSetupMfaMutation();
  const [enableMfa] = useEnableMfaMutation();
  const [regenerateBackupCodes] = useRegenerateBackupCodesMutation();
  const { data: mfaStatus, isSuccess: isStatusLoaded, isError: isStatusError } = useGetMfaStatusQuery();

  const [step, setStep] = useState(ONBOARDING_MFA_STEP.SETUP);
  const [setupData, setSetupData] = useState(null); // { provisioningUri, qrCode, secret }
  const [backupCodes, setBackupCodes] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const startedRef = useRef(false);

  // Kick off setup once, after the enrolled state is known. Guarded by a ref so the cache
  // flip that enrollCode triggers (enabled -> true) can't re-run this and yank the user off
  // the backup-codes screen.
  useEffect(() => {
    if (startedRef.current) return undefined;
    // Wait until we know whether MFA is already enabled (on error, fall through to setup —
    // the setup call surfaces its own loadError rather than leaving us hanging).
    if (!isStatusLoaded && !isStatusError) return undefined;
    startedRef.current = true;

    let cancelled = false;
    setLoadError(false);

    // Already enrolled on entry (e.g. a reload mid-flow): the one-time codes shown earlier are
    // gone, and silently advancing would let the user finish without recovery codes. Re-running
    // setup against an enabled account would also error. Issue a fresh set and show the
    // backup-codes step so they can save them; only advance if regeneration itself fails.
    if (mfaStatus?.enabled) {
      regenerateBackupCodes()
        .unwrap()
        .then((data) => {
          if (cancelled) return;
          setBackupCodes(data.backupCodes ?? []);
          setStep(ONBOARDING_MFA_STEP.BACKUP_CODES);
        })
        .catch(() => {
          if (!cancelled) onComplete?.();
        });
      return () => {
        cancelled = true;
      };
    }

    setupMfa()
      .unwrap()
      .then((data) => {
        if (!cancelled) setSetupData(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStatusLoaded, isStatusError]);

  // Lets an invalid-code rejection (422) propagate so MfaCodeInput can surface it.
  const enrollCode = async (code) => {
    const response = await enableMfa({ code: code.trim() }).unwrap();
    trackEvent('authentication:otp:setup_success');
    setBackupCodes(response.backupCodes ?? []);
    setStep(ONBOARDING_MFA_STEP.BACKUP_CODES);
  };

  // Enrollment flips the cached MFA status, so the onboarding gate stops diverting here;
  // hand control back to the walkthrough to continue (or complete) onboarding.
  const finish = () => {
    onComplete?.();
  };

  return { step, setupData, backupCodes, isSettingUp, loadError, enrollCode, finish };
};
